import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { departmentMatchesEscalation } from '../constants/ticketOptions';
import TicketDetailView from './TicketDetail.view';

// ── Attachments upload rules (Sprint 11 — email agent v2) ───────────────────
const ATTACH_ALLOWED_EXTS = ['png', 'jpg', 'jpeg', 'bmp', 'pdf', 'xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'mp4', 'mp3', 'mov'];
const ATTACH_MAX_FILES = 5;
const ATTACH_MAX_SIZE = 15 * 1024 * 1024; // 15MB

// Fetches an attachment through the authenticated download endpoint and renders
// it from a blob URL (a plain <img src> can't carry the JWT header). Kept in the
// container because it needs the api client; passed to the view as a prop.
function AuthThumb({ attachmentId, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    api.get(`/attachments/${attachmentId}/download`, { responseType: 'blob' })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachmentId]);
  if (!url) return <div className="h-16 w-16 flex-none animate-pulse rounded-field bg-paper-muted" />;
  return (
    <img src={url} alt={alt} onClick={() => window.open(url, '_blank')}
      className="h-16 w-16 flex-none cursor-pointer rounded-field border border-paper-border object-cover hover:opacity-80" />
  );
}

const slugify = (str) => str?.toLowerCase().replace(/\s+/g, '-') || '';

export default function TicketDetail() {
  // Two URL patterns:
  //   /tickets/:id           → portal DB id
  //   /tickets/hs/:hubspotId  → HubSpot ticket ID (backend upserts + returns portal row)
  const { id, hubspotId } = useParams();
  const apiPath = hubspotId ? `/tickets/hs/${hubspotId}` : `/tickets/${id}`;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [deptActions, setDeptActions] = useState([]);
  const [hubspotEnrichment, setHubspotEnrichment] = useState({ content: '', contactEmail: '', contactName: '', deals: [] });
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  // Per-action loading flags (consumed by the view).
  const [savingEdit, setSavingEdit] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [financeConfirming, setFinanceConfirming] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [submittingDeptResponse, setSubmittingDeptResponse] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [advancingSampleStage, setAdvancingSampleStage] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(apiPath);
      setTicket(res.data);
      try {
        const dar = await api.get(`/tickets/${res.data.id}/dept-actions`);
        setDeptActions(Array.isArray(dar.data) ? dar.data : []);
      } catch { setDeptActions([]); }
    } catch (err) {
      if (err.response?.status === 404) navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reassign-eligible callers fetch the ticket-scoped assignable list.
  // /tickets/:id/assignable-users returns {id, name, email, display_name, role, department}
  // filtered by ticket entity + (dept_manager only) caller department. Others 403;
  // fail-soft to [] so the page renders even for ineligible roles.
  const canReassign = ['super_admin', 'entity_manager', 'dept_manager'].includes(user?.role);
  useEffect(() => {
    if (!ticket?.id || !canReassign) { setAssignableUsers([]); return; }
    api.get(`/tickets/${ticket.id}/assignable-users`)
      .then((r) => setAssignableUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAssignableUsers([]));
  }, [ticket?.id, ticket?.department_assigned, canReassign]);

  // HubSpot enrichment: original message (content) + contact email/name.
  //   1. hubspot_ticket_id → GET /hubspot/ticket-detail/:id?mobile=… (ticket assoc, then mobile fallback)
  //   2. else customer_mobile → GET /hubspot/contact-email?mobile=… (email/name only)
  useEffect(() => {
    if (!ticket) return;
    const hasHsId = Boolean(ticket.hubspot_ticket_id);
    const hasMobile = Boolean(ticket.customer_mobile);
    const needEmail = !ticket.customer_email;
    if (!hasHsId && (!hasMobile || !needEmail)) return;

    setEnrichmentLoading(true);
    if (hasHsId) {
      const mobileParam = hasMobile ? `?mobile=${encodeURIComponent(ticket.customer_mobile)}` : '';
      api.get(`/hubspot/ticket-detail/${ticket.hubspot_ticket_id}${mobileParam}`)
        .then((r) => setHubspotEnrichment((prev) => ({ ...prev, ...(r.data || {}) })))
        .catch(() => {})
        .finally(() => setEnrichmentLoading(false));
    } else {
      api.get(`/hubspot/contact-email?mobile=${encodeURIComponent(ticket.customer_mobile)}`)
        .then((r) => {
          const { email = '', name = '' } = r.data || {};
          if (email || name) setHubspotEnrichment((prev) => ({ ...prev, contactEmail: email, contactName: name }));
        })
        .catch(() => {})
        .finally(() => setEnrichmentLoading(false));
    }
  }, [ticket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers (each preserves the exact endpoint from the pre-redesign file) ──
  const handleSaveEdit = async (patch) => {
    setSavingEdit(true);
    try { await api.put(`/tickets/${ticket?.id || id}`, patch); await load(); toast.success('Ticket updated'); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to update ticket'); }
    finally { setSavingEdit(false); }
  };

  const handleAddNote = async (text, noteType) => {
    if (!text?.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/tickets/${ticket?.id || id}/notes`, noteType ? { note: text, note_type: noteType, is_internal: true } : { note: text });
      await load(); toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setAddingNote(false); }
  };

  const handleResolve = async ({ resolution_taken, responsibility_attribution, notes } = {}) => {
    if (!resolution_taken) { toast.error('Please select a Resolution Taken option'); return; }
    if (!responsibility_attribution) { toast.error('Please select who is responsible for this issue'); return; }
    setResolving(true);
    try {
      await api.patch(`/tickets/${ticket?.id || id}/resolve`, {
        resolution_taken, responsibility_attribution, notes, changed_by: user?.id,
      });
      await load(); toast.success('Ticket marked resolved');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to resolve ticket'); }
    finally { setResolving(false); }
  };

  const handleAttributionEdit = async (newValue) => {
    try {
      await api.put(`/tickets/${ticket?.id || id}`, { responsibility_attribution: newValue });
      await load(); toast.success('Responsibility updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update responsibility');
    }
  };

  const handleMoveToPendingFinance = async () => {
    setResolving(true);
    try { await api.put(`/tickets/${ticket?.id || id}`, { status: 'pending_finance' }); await load(); toast.success('Ticket moved to Pending Finance'); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to update status'); }
    finally { setResolving(false); }
  };

  const handleFinanceConfirm = async () => {
    setFinanceConfirming(true);
    try { await api.patch(`/tickets/${ticket?.id || id}/finance-confirm`, { changed_by: user?.id }); await load(); toast.success('Finance confirmed — ticket moved to Resolved'); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to confirm'); }
    finally { setFinanceConfirming(false); }
  };

  const handleClose = async ({ customer_contacted, contact_method, closing_notes }) => {
    if (!closing_notes?.trim()) { toast.error('Closing notes are required'); return; }
    setClosing(true);
    try {
      await api.post(`/tickets/${ticket?.id || id}/close`, { actor_id: user?.id, actor_name: user?.name, customer_contacted, contact_method, closing_notes });
      await load(); toast.success('Ticket closed');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to close ticket'); }
    finally { setClosing(false); }
  };

  const handleReopen = async (reason) => {
    if (!reason?.trim()) { toast.error('Reopen reason is required'); return; }
    setReopening(true);
    try {
      await api.post(`/tickets/${ticket?.id || id}/reopen`, { actor_id: user?.id, actor_name: user?.name, reason });
      await load(); toast.success('Ticket reopened');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to reopen ticket'); }
    finally { setReopening(false); }
  };

  const handleEscalate = async ({ department, reason }) => {
    if (!department) { toast.error('Please select a department'); return; }
    setEscalating(true);
    try {
      await api.post(`/tickets/${ticket?.id || id}/escalate`, { dept_slug: department, actor_id: user?.id, actor_name: user?.name, actor_role: user?.role, reason });
      await load(); toast.success('Ticket escalated');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to escalate ticket'); }
    finally { setEscalating(false); }
  };

  const handleAdvanceSampleStage = async (nextStage) => {
    setAdvancingSampleStage(true);
    try {
      await api.post(`/tickets/${ticket?.id || id}/sample-stage`, { sample_stage: nextStage });
      await load();
      toast.success(nextStage === 'none' ? 'Sample stage reset' : `Sample marked: ${nextStage}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update sample stage');
    } finally { setAdvancingSampleStage(false); }
  };

  const handleSubmitDeptResponse = async ({ response, action_taken, confirmed_done, files }) => {
    if (!response?.trim()) { toast.error('Response text is required'); return; }
    setSubmittingDeptResponse(true);
    try {
      const fd = new FormData();
      fd.append('dept_user_id', user?.id);
      fd.append('dept_user_name', user?.name);
      fd.append('department_slug', user?.department || ticket?.escalated_to_dept);
      fd.append('response', response);
      fd.append('action_taken', action_taken || '');
      fd.append('confirmed_done', confirmed_done ? 'true' : 'false');
      (files || []).forEach((f) => fd.append('files', f));
      await api.post(`/tickets/${ticket?.id || id}/dept-response`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load(); toast.success('Department response submitted');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to submit response'); }
    finally { setSubmittingDeptResponse(false); }
  };

  const handleSalesOutcome = async ({ outcome, note, target_status }) => {
    if (!outcome) return;
    setAddingNote(true);
    const noteText = `Sales Call Outcome: ${outcome.label}${note?.trim() ? '\n' + note.trim() : ''}`;
    try {
      await api.post(`/tickets/${ticket?.id || id}/notes`, { note: noteText, note_type: 'sales_call_outcome', is_internal: true });
      await api.put(`/tickets/${ticket?.id || id}`, { status: target_status });
      await load(); toast.success('Sales call outcome recorded');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to record outcome'); }
    finally { setAddingNote(false); }
  };

  const handleFieldEdit = async (field, value) => {
    try { await api.put(`/tickets/${ticket.id}`, { [field]: value }); await load(); toast.success('Updated'); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to update'); }
  };

  const handleUploadAttachment = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (list.length > ATTACH_MAX_FILES) { toast.error(`Maximum ${ATTACH_MAX_FILES} files per upload.`); return; }
    const badExt = list.filter((f) => !ATTACH_ALLOWED_EXTS.includes((f.name.split('.').pop() || '').toLowerCase()));
    if (badExt.length) { toast.error(`File type not allowed: ${badExt.map((f) => f.name).join(', ')}`); return; }
    const tooBig = list.filter((f) => f.size > ATTACH_MAX_SIZE);
    if (tooBig.length) { toast.error(`Over 15MB: ${tooBig.map((f) => f.name).join(', ')}`); return; }
    const fd = new FormData();
    list.forEach((f) => fd.append('files', f));
    setUploadingAttachment(true);
    try {
      await api.post(`/tickets/${ticket.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load(); toast.success(`${list.length} file${list.length > 1 ? 's' : ''} uploaded`);
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setUploadingAttachment(false); }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-status-under-process border-t-transparent" />
      </div>
    );
  }
  if (!ticket) return null;

  // ── role/permission flags (computed in the container) ──
  const canEdit = user?.role !== 'agent' || ticket.created_by === user.id;
  const canManageTicket = ['supervisor', 'super_admin', 'entity_manager'].includes(user?.role);
  const canEditAttribution = ['super_admin', 'entity_manager', 'dept_manager'].includes(user?.role);
  const isDeptUser = ['dept_user', 'dept_manager', 'logistics_manager'].includes(user?.role);
  const canConfirmFinance = ['dept_manager', 'super_admin'].includes(user?.role);
  // Dept response form gate (preserves live logic: matching dept + open status).
  const canRespondAsDept = isDeptUser && Boolean(ticket.escalated_to_dept)
    && departmentMatchesEscalation(user?.department, ticket.escalated_to_dept)
    && ['escalated', 'dept_action_pending'].includes(ticket.status);

  return (
    <TicketDetailView
      ticket={ticket}
      notes={ticket.notes || []}
      statusHistory={ticket.history || []}
      deptActions={deptActions}
      thirdPartySubmissions={ticket.third_party_submissions || []}
      attachments={ticket.attachments || []}
      hubspotEnrichment={{
        message: hubspotEnrichment.content || '',
        contactEmail: hubspotEnrichment.contactEmail || '',
        contactName: hubspotEnrichment.contactName || '',
        deals: hubspotEnrichment.deals || [],
      }}
      enrichmentLoading={enrichmentLoading}
      assignableUsers={assignableUsers}
      canReassign={canReassign}
      canEditAttribution={canEditAttribution}
      canEdit={canEdit}
      canManageTicket={canManageTicket}
      isDeptUser={isDeptUser}
      canConfirmFinance={canConfirmFinance}
      canRespondAsDept={canRespondAsDept}
      resolving={resolving}
      financeConfirming={financeConfirming}
      closing={closing}
      reopening={reopening}
      escalating={escalating}
      savingEdit={savingEdit}
      addingNote={addingNote}
      submittingDeptResponse={submittingDeptResponse}
      uploadingAttachment={uploadingAttachment}
      advancingSampleStage={advancingSampleStage}
      currentUser={user}
      onAdvanceSampleStage={handleAdvanceSampleStage}
      onSaveEdit={handleSaveEdit}
      onAddNote={handleAddNote}
      onResolve={handleResolve}
      onAttributionEdit={handleAttributionEdit}
      onMoveToPendingFinance={handleMoveToPendingFinance}
      onFinanceConfirm={handleFinanceConfirm}
      onClose={handleClose}
      onReopen={handleReopen}
      onEscalate={handleEscalate}
      onSubmitDeptResponse={handleSubmitDeptResponse}
      onSalesOutcome={handleSalesOutcome}
      onFieldEdit={handleFieldEdit}
      onUploadAttachment={handleUploadAttachment}
      onDownloadAttachment={handleDownloadAttachment}
      AuthThumb={AuthThumb}
    />
  );
}
